import React from "react";
import { useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../config";
import Faq from "./Faq";
import icon1 from "../image/gym1.png";
import icon2 from "../image/gym2.png";
import { Link } from "react-router-dom";
import playstore from "../image/playstore.png";
import appstore from "../image/appstore.png";
import { Helmet, HelmetProvider } from "react-helmet-async";

export default function GymListing() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gymName, setGymName] = useState("");

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    phone: "",
    city: "",
    message: "",
    owner_name: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(
        `${config.apiUrl}/business-list-request`,
        formData,
        { headers: { "Content-Type": "application/json" } }
      );
      setGymName(formData.name);
      setFormData({ name: "", address: "", email: "", phone: "", city: "", message: "", owner_name: "" });
      setErrors({});
      setSubmitted(true);
    } catch (error) {
      if (error.response?.status === 422) {
        const e = error.response.data.errors || {};
        setErrors({
          name:       e.name?.[0]       || "",
          owner_name: e.owner_name?.[0] || "",
          email:      e.email?.[0]      || "",
          phone:      e.phone?.[0]      || "",
          city:       e.city?.[0]       || "",
          address:    e.address?.[0]    || "",
        });
        toast.error(error.response.data.message || "Please fix the errors below.", { position: "top-right", autoClose: 3000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Free Gym Listing — MeraGym",
    description: "List your gym or fitness center for free on MeraGym. Reach thousands of fitness seekers, get leads, and grow your gym business online at zero cost.",
    provider: { "@type": "Organization", name: "MeraGym", url: "https://meragym.com" },
    serviceType: "Gym Directory Listing",
    areaServed: { "@type": "Country", name: "India" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "INR", description: "Free gym listing forever" },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://meragym.com" },
      { "@type": "ListItem", position: 2, name: "Free Gym Listing", item: "https://meragym.com/free-gym-listing" },
    ],
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Free Gym Listing India — Register Your Gym Online | MeraGym</title>
          <meta name="description" content="List your gym or fitness center on MeraGym for free. Reach thousands of fitness seekers in your city, get real leads and enquiries online. 100% free — no credit card needed." />
          <meta name="keywords" content="free gym listing India, register gym online, gym directory India, list gym for free, gym listing website India, add gym online, gym owner registration, fitness center listing" />
          <link rel="canonical" href="https://meragym.com/free-gym-listing" />
          <meta property="og:title" content="Free Gym Listing India — Register Your Gym Online | MeraGym" />
          <meta property="og:description" content="List your gym for free on MeraGym and start getting leads from fitness seekers near you. 5,000+ gyms already listed." />
          <meta property="og:url" content="https://meragym.com/free-gym-listing" />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Free Gym Listing India | MeraGym" />
          <meta name="twitter:description" content="Register your gym for free and get leads online. Join 5,000+ gyms on MeraGym." />
          <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        </Helmet>
      </HelmetProvider>

      <ToastContainer />

      {/* ── Hero / Registration Form ── */}
      <div className="gym-listing-section">
        <div className="bg-overlay"></div>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5 col-md-12 position-relative my-auto">
              <div className="listing-intro">
                <span className="listing-intro-badge">100% Free · No Credit Card</span>
                <h1>List Your Gym &amp; Fitness Center Online for Free</h1>
                <p>
                  List your <b className="text-danger">Gym</b> or Fitness Center for free and start getting leads &amp; enquiries online today.
                </p>
                <div className="listing-intro-stats">
                  <div className="listing-intro-stat">
                    <strong>5,000+</strong>
                    <span>Gyms Listed</span>
                  </div>
                  <div className="listing-intro-stat">
                    <strong>50+</strong>
                    <span>Cities</span>
                  </div>
                  <div className="listing-intro-stat">
                    <strong>24h</strong>
                    <span>Review Time</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-7 col-md-12">
              <div className="listing-form-card">

                {submitted ? (
                  <div className="listing-success">
                    <div className="listing-success-icon">
                      <i className="bi bi-check-circle-fill"></i>
                    </div>
                    <h2>Your Gym is Registered!</h2>
                    <p>
                      <strong>{gymName}</strong> has been successfully submitted. Our team will review and publish your listing within <strong>24 hours</strong>.
                    </p>
                    <div className="listing-success-steps">
                      <div className="listing-success-step">
                        <i className="bi bi-check2"></i>
                        <span>Details saved</span>
                      </div>
                      <div className="listing-success-step">
                        <i className="bi bi-hourglass-split"></i>
                        <span>Under review</span>
                      </div>
                      <div className="listing-success-step">
                        <i className="bi bi-rocket-takeoff"></i>
                        <span>Goes live in 24h</span>
                      </div>
                    </div>
                    <button
                      className="listing-success-btn"
                      onClick={() => { setSubmitted(false); setGymName(""); }}
                    >
                      Register Another Gym
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="listing-form-heading">
                      <h2>Register Your Gym — Free</h2>
                      <p>Fill in the details below and your gym will be live within 24 hours.</p>
                    </div>

                    <form className="usr-form" onSubmit={handleSubmit} noValidate>
                      <div className="row contact-user-row">
                        <div className="col-sm-8 col-md-8">
                          <label className="listing-form-label">Gym Name <span className="text-danger">*</span></label>
                          <input type="text" className="form-control" placeholder="e.g. FitZone Gym" name="name" value={formData.name} onChange={handleChange} />
                          {errors.name && <small className="text-danger">{errors.name}</small>}
                        </div>
                        <div className="col-sm-4 col-md-4 mt-sm-20">
                          <label className="listing-form-label">Owner Name <span className="text-danger">*</span></label>
                          <input type="text" className="form-control" placeholder="Your name" name="owner_name" value={formData.owner_name} onChange={handleChange} />
                          {errors.owner_name && <small className="text-danger">{errors.owner_name}</small>}
                        </div>
                      </div>

                      <div className="row contact-user-row">
                        <div className="col-sm-4 col-md-4">
                          <label className="listing-form-label">Email Address <span className="text-danger">*</span></label>
                          <input type="email" className="form-control" placeholder="you@example.com" name="email" value={formData.email} onChange={handleChange} />
                          {errors.email && <small className="text-danger">{errors.email}</small>}
                        </div>
                        <div className="col-sm-4 col-md-4 mt-sm-20">
                          <label className="listing-form-label">Phone Number <span className="text-danger">*</span></label>
                          <input type="tel" className="form-control" placeholder="9XXXXXXXXX" name="phone" value={formData.phone} onChange={handleChange} />
                          {errors.phone && <small className="text-danger">{errors.phone}</small>}
                        </div>
                        <div className="col-sm-4 col-md-4 mt-sm-20">
                          <label className="listing-form-label">City <span className="text-danger">*</span></label>
                          <input type="text" className="form-control" placeholder="Varanasi" name="city" value={formData.city} onChange={handleChange} />
                          {errors.city && <small className="text-danger">{errors.city}</small>}
                        </div>
                      </div>

                      <div className="row contact-user-row">
                        <div className="col-sm-12">
                          <label className="listing-form-label">Full Gym Address <span className="text-danger">*</span></label>
                          <textarea className="form-control" rows="2" name="address" value={formData.address} onChange={handleChange} placeholder="Street, Area, City — full address"></textarea>
                          {errors.address && <small className="text-danger">{errors.address}</small>}
                        </div>
                      </div>

                      <div className="row contact-user-row">
                        <div className="col-sm-12">
                          <button
                            className="btn btn-danger btn-block w-100 submit-btn rounded-1"
                            type="submit"
                            disabled={submitting}
                          >
                            {submitting
                              ? <><span className="listing-spinner"></span> Registering…</>
                              : <>Register My Gym Free <i className="bi bi-check-circle ms-2"></i></>
                            }
                          </button>
                          <div className="listing-form-trust">
                            <span><i className="bi bi-shield-fill-check text-success"></i> Secure</span>
                            <span><i className="bi bi-gift-fill text-danger"></i> Free Forever</span>
                            <span><i className="bi bi-clock-fill text-warning"></i> Live in 24h</span>
                          </div>
                        </div>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats / Trust Bar ── */}
      <div className="listing-stats-bar">
        <div className="container">
          <div className="listing-stats-grid">
            <div className="listing-stats-item">
              <i className="bi bi-buildings-fill"></i>
              <strong>5,000+</strong>
              <span>Gyms Listed</span>
            </div>
            <div className="listing-stats-item">
              <i className="bi bi-geo-alt-fill"></i>
              <strong>50+</strong>
              <span>Cities Covered</span>
            </div>
            <div className="listing-stats-item">
              <i className="bi bi-person-check-fill"></i>
              <strong>1 Lakh+</strong>
              <span>Monthly Visitors</span>
            </div>
            <div className="listing-stats-item">
              <i className="bi bi-gift-fill"></i>
              <strong>₹0</strong>
              <span>Listing Cost</span>
            </div>
            <div className="listing-stats-item">
              <i className="bi bi-lightning-charge-fill"></i>
              <strong>24 hrs</strong>
              <span>Go Live Time</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── How It Works ── */}
      <section className="listing-how-section">
        <div className="container">
          <div className="listing-section-header">
            <span className="listing-section-pill">Simple Process</span>
            <h2>How to List Your Gym — 3 Easy Steps</h2>
            <p>Get your gym visible to thousands of fitness seekers in your city within 24 hours.</p>
          </div>
          <div className="listing-how-grid">
            <div className="listing-how-card">
              <div className="listing-how-num">01</div>
              <div className="listing-how-icon"><i className="bi bi-pencil-square"></i></div>
              <h3>Fill the Form</h3>
              <p>Enter your gym name, owner details, city, address, and contact information in the registration form above.</p>
            </div>
            <div className="listing-how-arrow"><i className="bi bi-arrow-right"></i></div>
            <div className="listing-how-card">
              <div className="listing-how-num">02</div>
              <div className="listing-how-icon"><i className="bi bi-images"></i></div>
              <h3>Upload Photos</h3>
              <p>Add your gym's thumbnail, cover photo, and gallery images to make your profile attractive to visitors.</p>
            </div>
            <div className="listing-how-arrow"><i className="bi bi-arrow-right"></i></div>
            <div className="listing-how-card">
              <div className="listing-how-num">03</div>
              <div className="listing-how-icon"><i className="bi bi-graph-up-arrow"></i></div>
              <h3>Get Leads</h3>
              <p>Your gym goes live within 24 hours. Start receiving real enquiries and leads from fitness seekers near you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Benefits for Gym Owners ── */}
      <section className="listing-benefits-section">
        <div className="container">
          <div className="listing-section-header">
            <span className="listing-section-pill">Why Choose Us</span>
            <h2>Everything a Gym Owner Needs to Grow Online</h2>
            <p>MeraGym gives gym owners the digital tools to attract members and manage their presence — all for free.</p>
          </div>
          <div className="listing-benefits-grid">
            {[
              { icon: "bi-search", title: "Get Discovered", desc: "Appear in search results when people look for gyms in your city or area." },
              { icon: "bi-envelope-check-fill", title: "Free Leads & Enquiries", desc: "Receive real membership enquiries directly from interested fitness seekers." },
              { icon: "bi-star-fill", title: "Build Your Reputation", desc: "Collect reviews and ratings to build trust and stand out from competitors." },
              { icon: "bi-phone-fill", title: "Mobile App Visibility", desc: "Your gym profile appears on the MeraGym mobile app used by thousands daily." },
              { icon: "bi-cpu-fill", title: "Gym Software Access", desc: "Get access to our basic gym management software to handle members and billing." },
              { icon: "bi-megaphone-fill", title: "Special Offers Platform", desc: "Promote membership deals and discounts to attract new members faster." },
              { icon: "bi-bar-chart-fill", title: "Performance Insights", desc: "Track how many people viewed your profile and enquired about memberships." },
              { icon: "bi-shield-check", title: "Verified Badge", desc: "Get a verified gym badge after review — builds instant credibility with users." },
            ].map((b, i) => (
              <div className="listing-benefit-card" key={i}>
                <div className="listing-benefit-icon"><i className={`bi ${b.icon}`}></i></div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Join (original p2 — improved) ── */}
      <div className="gym-listing-p2">
        <div className="container">
          <div className="listing-p2-intro">
            <h2>"Elevate Your Workout Journey: Join MeraGym for Unmatched Fitness Excellence!"</h2>
            <p>Here is why you should list your gym on MeraGym</p>
          </div>
          <div className="row listing-p2-row">
            <div className="col-sm-6 col-md-6 listing-p2-col">
              <h3>Get Gym Software</h3>
              <ul>
                <li>Basic gym management tool</li>
                <li>Payment management</li>
                <li>Tailored solutions including unique features and integrations</li>
                <li>Attendance tracking</li>
                <li>Attendance report &amp; analysis</li>
                <li>Data security and compliance features</li>
                <li>Priority technical &amp; training support</li>
              </ul>
            </div>
            <div className="col-sm-6 col-md-6 listing-p2-col">
              <div><img src={icon1} alt="Gym software dashboard" /></div>
            </div>
            <div className="col-sm-6 col-md-6 listing-p2-col">
              <div><img src={icon2} alt="Fitness center discovery" /></div>
            </div>
            <div className="col-sm-6 col-md-6 listing-p2-col">
              <h3>Discover the Best Fitness Experiences</h3>
              <ul>
                <li>Gain access to an extensive network of top-notch gyms and fitness centers</li>
                <li>Explore our regularly updated blog for expert advice and workout tips</li>
                <li>Enjoy special deals and discounts offered exclusively through MeraGym</li>
                <li>Dive into comprehensive gym profiles featuring facilities and equipment</li>
                <li>View gym hours seamlessly, making fitness planning hassle-free</li>
                <li>Share tips and get recommendations from fellow members</li>
                <li>Tailor your fitness routine with the flexibility to explore different gyms</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ── Testimonials ── */}
      <section className="listing-testimonials-section">
        <div className="container">
          <div className="listing-section-header">
            <span className="listing-section-pill">Success Stories</span>
            <h2>What Gym Owners Say About MeraGym</h2>
            <p>Real experiences from gym owners who grew their business by listing on MeraGym.</p>
          </div>
          <div className="listing-testimonials-grid">
            <div className="listing-testimonial-card">
              <div className="listing-testimonial-stars">
                {[...Array(5)].map((_, i) => <i key={i} className="bi bi-star-fill"></i>)}
              </div>
              <p>"Since listing on MeraGym, we get 15–20 genuine membership enquiries every month. The profile setup was super easy and completely free. Best decision for our gym's online presence."</p>
              <div className="listing-testimonial-author">
                <div className="listing-testimonial-avatar">R</div>
                <div>
                  <strong>Rahul Sharma</strong>
                  <span>Owner, PowerFit Gym — Varanasi</span>
                </div>
              </div>
            </div>
            <div className="listing-testimonial-card">
              <div className="listing-testimonial-stars">
                {[...Array(5)].map((_, i) => <i key={i} className="bi bi-star-fill"></i>)}
              </div>
              <p>"MeraGym helped us double our walk-ins within 3 months. The verified badge gave us instant credibility. I recommend every gym owner to list here — it's truly free and worth it."</p>
              <div className="listing-testimonial-author">
                <div className="listing-testimonial-avatar">P</div>
                <div>
                  <strong>Priya Nair</strong>
                  <span>Owner, ZenFit Studio — Noida</span>
                </div>
              </div>
            </div>
            <div className="listing-testimonial-card">
              <div className="listing-testimonial-stars">
                {[...Array(5)].map((_, i) => <i key={i} className="bi bi-star-fill"></i>)}
              </div>
              <p>"The gym software access that came with the free listing is a bonus. We now manage our entire member billing and attendance through MeraGym. Highly recommended for gym owners."</p>
              <div className="listing-testimonial-author">
                <div className="listing-testimonial-avatar">A</div>
                <div>
                  <strong>Amit Verma</strong>
                  <span>Owner, IronCore Fitness — Lucknow</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Faq page="free_gym_listing" />

      {/* ── App Download ── */}
      <div className="app-dwnlod-area">
        <div className="dwnlod-p4">
          <h4>Download our fitness app and redefine your journey to greatness!</h4>
          <h6 className="mobl-h6">Download our fitness app</h6>
          <div className="dwnlod-p4-item">
            <a href="https://apps.apple.com" target="_blank" rel="noreferrer">
              <img src={appstore} alt="Download MeraGym on App Store" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.techup.mygym" target="_blank" rel="noreferrer">
              <img src={playstore} alt="Download MeraGym on Google Play" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
